import { useState, useRef, useEffect, useCallback } from 'react'
import { Search, Clock } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useRecentSearches } from '@/hooks/use-recent-searches'
import { cn } from '@/lib/utils'

interface SearchWithRecentProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  ariaLabel: string
  entityKey: string
  className?: string
}

export function SearchWithRecent({
  value,
  onChange,
  placeholder = 'Search...',
  ariaLabel,
  entityKey,
  className,
}: SearchWithRecentProps) {
  const { recentSearches, addSearch, clearSearches } = useRecentSearches(entityKey)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const blurTimerRef = useRef<ReturnType<typeof setTimeout>>()

  // Save search term after 1 second of inactivity
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    if (value.trim().length >= 2) {
      saveTimerRef.current = setTimeout(() => {
        addSearch(value)
      }, 1000)
    }
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [value, addSearch])

  const handleFocus = () => {
    if (recentSearches.length > 0) {
      setIsOpen(true)
      setSelectedIndex(-1)
    }
  }

  const handleBlur = () => {
    blurTimerRef.current = setTimeout(() => setIsOpen(false), 200)
  }

  const selectItem = useCallback(
    (term: string) => {
      onChange(term)
      setIsOpen(false)
      setSelectedIndex(-1)
      inputRef.current?.focus()
    },
    [onChange]
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || recentSearches.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev < recentSearches.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : recentSearches.length - 1))
        break
      case 'Enter': {
        const selected = recentSearches[selectedIndex]
        if (selectedIndex >= 0 && selected) {
          e.preventDefault()
          selectItem(selected)
        }
        break
      }
      case 'Escape':
        setIsOpen(false)
        setSelectedIndex(-1)
        break
    }
  }

  const handleClear = () => {
    clearSearches()
    setIsOpen(false)
    inputRef.current?.focus()
  }

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current)
    }
  }, [])

  const showDropdown = isOpen && recentSearches.length > 0

  return (
    <div ref={containerRef} className={cn('relative w-full max-w-sm', className)}>
      <Search
        className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        ref={inputRef}
        placeholder={placeholder}
        className="pl-8"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        aria-expanded={showDropdown}
        aria-controls={showDropdown ? `recent-searches-${entityKey}` : undefined}
        aria-activedescendant={
          showDropdown && selectedIndex >= 0
            ? `recent-search-${entityKey}-${selectedIndex}`
            : undefined
        }
        aria-haspopup="listbox"
        role="combobox"
        autoComplete="off"
      />
      {showDropdown && (
        <div
          id={`recent-searches-${entityKey}`}
          className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover shadow-md"
          role="listbox"
          aria-label="Recent searches"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <span className="text-xs font-medium text-muted-foreground">Recent Searches</span>
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm px-1"
              aria-label="Clear recent searches"
            >
              Clear
            </button>
          </div>
          {recentSearches.map((term, index) => (
            <div
              key={term}
              id={`recent-search-${entityKey}-${index}`}
              role="option"
              aria-selected={index === selectedIndex}
              className={cn(
                'flex items-center gap-2 px-3 py-2 text-sm cursor-pointer',
                index === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
              )}
              onMouseDown={(e) => {
                e.preventDefault()
                selectItem(term)
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <Clock className="h-3 w-3 text-muted-foreground shrink-0" aria-hidden="true" />
              <span className="truncate">{term}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
